import * as React from "react";
import { useMemo, useState } from "react";
import { styled } from "@mui/material";
import { useDiseasesSections } from "./hooks/useDiseasesSections";
import { Suggestion } from "./hooks/useSuggestions";
import { DiseaseCategoryType } from "fe-common/graphql";
import { CATEGORY } from "fe-common/analytics/analytics";
import { PlanSection } from "./recommendedPlan/planSection";
import { useSuggestionsForPlan } from "./hooks/useSuggestionsForPlan";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { RosebudMobileBottomNavigationHeight } from "./common/rosebudButtomNavigationButton";
import { Route, Switch, useRouteMatch } from "react-router";
import { removeTrailingSlash } from "fe-common/routes/removeTrailingSlash";
import { PlanTabsEnum } from "./editPanel/editPanelPage";

//region [[ Styles ]]
const PlanView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  minHeight: "300px",
  backgroundColor: "transparent",
  padding: 0,

  "& .plan-item": {
    background: KahunColorMap.White,
    borderRadius: "9.4px",
    boxShadow: "0px 1.1747px 9.39759px rgba(0, 0, 0, 0.12)",
  },
  "& .in-cluster": {
    borderTop: "1px solid " + KahunColorMap.AccentLight,
    boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.12)",
  },
  "& .plan-item > div": {
    paddingTop: "15px",
    paddingBottom: "15px",
  },
  "& .plan-item .lab-test": {
    paddingTop: "0",
    paddingBottom: "0",
  },
  "& .plan-item:last-of-kind": {
    marginBottom: 0,
  },
  "& .lab-test > *": {
    flexWrap: "wrap",
  },
}));

const MobilePlanSection = styled(PlanSection)({
  boxShadow: "none",
  background: KahunColorMap.White,
  height: "100%",
  marginBottom: `-${RosebudMobileBottomNavigationHeight}px`,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PlanProps {
  className?: string;
  maxItemsPerCategory?: number;
}
//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]
const maxItems = 10;

export const Plan = ({ maxItemsPerCategory, ...props }: PlanProps) => {
  let {
    findingSuggestions: nextStepSuggestions,
    planIsLoading,
    clusteredSuggestions,
  } = useSuggestionsForPlan();

  const match = useRouteMatch();
  // Clean "/" from path in the end if exists
  const matchPath = removeTrailingSlash(match.path);

  const { loading: ddIsLoading } = useDiseasesSections();

  const [categoryType, setCategoryType] = useState(
    DiseaseCategoryType.OutcomeTest,
  );

  const loading = ddIsLoading || planIsLoading;

  const filterSuggestions = (suggestion: Suggestion) => {
    return true;
  };

  const filteredNextStepSuggestions = useMemo(
    () => nextStepSuggestions.filter(filterSuggestions),
    [nextStepSuggestions],
  );

  const onChangeCategory = (newCategory: DiseaseCategoryType) => {
    setCategoryType(newCategory);
  };

  return (
    <PlanView className={props.className}>
      <Switch>
        <Route
          path={`${matchPath}/:tabName?`}
          exact
          render={(props) => {
            const tabName = (props.match.params as { tabName?: string })
              .tabName;

            const category =
              tabName === PlanTabsEnum.IMAGING
                ? DiseaseCategoryType.Imaging
                : DiseaseCategoryType.OutcomeTest;

            return (
              <MobilePlanSection
                key={categoryType}
                eventCategory={CATEGORY.IMPROVE_DIAGNOSIS}
                isLoading={loading}
                category={category}
                suggestions={filteredNextStepSuggestions}
                clusteredSuggestions={clusteredSuggestions}
                title={"Suggested lab workup "}
                maxItems={maxItems}
                onChangeCategory={onChangeCategory}
              />
            );
          }}
        />
      </Switch>
    </PlanView>
  );
};
